import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  type ValidationErrors,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxChange, MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatTabsModule } from "@angular/material/tabs";
import { Router } from "@angular/router";
import { decimalNumber, phoneNumberPattern } from "@app/regex";
import { contactInfoMessage, findContactInfo } from "@app/utils/contact-info.util";
import {
  IMediaPicker,
  MediaPickerComponent,
} from "@components/media-picker/media-picker.component";
import {
  categoryNameOf,
  getSegmentForCategory,
  isLivestockCategoryName,
  LIVESTOCK_SEGMENT,
  TANK_SEGMENT,
} from "@components/product-input/category-segments";
import { ProductOptionsComponent } from "@components/product-options/product-options.component";
import {
  ALL_ZONE_CONTROL_NAMES,
  deliveryZonesFor,
  highestZoneFee,
  type ZoneFees,
  zoneControlName,
} from "@components/us-delivery-zones";
import { ngxEditorConfig } from "@config/angular-editor.config";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { IApiResponse } from "@models/api-response.model";
import {
  ICategoryList,
  IProductOptionRequestDto,
  IProductRequestDto,
  IProductResponseDto,
} from "@models/product-list.model";
import { AppStateService, RequestService } from "@services/index";
import { ProductService } from "@services/product/product.service";
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  of,
  Subscription,
  startWith,
  switchMap,
} from "rxjs";
import { Country } from "../../models/country.model";
import { printFormValueAndValidity } from "../../utils";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "../filters/form-autocomplete/form-autocomplete.component";
import { ProductControls, ProductListingStatus } from "./product-controls.enum";

export interface ICountrySpecificInfo {
  iso2: string;
  displayPrice: number;
  deliveryCharge: number;
  destinationTranshipperMarkup: number;
  productOptions: IProductOptionRequestDto[];
}

/**
 * No contact details (phone numbers, WhatsApp, Telegram, social links, handles,
 * email) in the product name or description - the server refuses them too, with
 * the same message. The error carries the message to show.
 */
function noContactInfo(control: AbstractControl): ValidationErrors | null {
  const found = findContactInfo(control.value);
  return found.length ? { contactInfo: contactInfoMessage(found) } : null;
}

@Component({
  selector: "fh-product-input",
  templateUrl: "./product-input.component.html",
  styleUrls: ["./product-input.component.css"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    FormAutocompleteComponent,
    ProductOptionsComponent,
    AngularEditorModule,
    MediaPickerComponent,
    MatChipsModule,
    MatIconModule,
    MatCheckboxModule,
    MatTabsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatExpansionModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
})
export class ProductInputComponent implements OnInit, OnDestroy {
  @Input() controls: ProductControls[] = [];
  @Input() formTitle!: string;
  @Input() categoryId!: number;
  @Input() productId = 0;
  @Input() userId!: string;
  @Output() formChanged = new EventEmitter<IProductRequestDto>();
  @Output() formIsValid = new EventEmitter<boolean>();
  /**
   * The form segment to show (LIVESTOCK_SEGMENT / TANK_SEGMENT / OTHER_SEGMENT),
   * not the product's category id — several main categories share one form.
   * Emitted from getCategories(), once the category names are known.
   */
  @Output() productCategorySegment = new EventEmitter<number>();

  // The description is rich text, like SEO pages and blogs: bold, italic,
  // underline, headings, lists, links, alignment and the <> source view.
  // Fonts, colours and sizes stay hidden so descriptions keep the site's own
  // style, and images and videos stay with the product's media, not inline.
  editorConfig = {
    ...ngxEditorConfig,
    enableToolbar: true,
    showToolbar: true,
    toolbarHiddenButtons: [
      ["subscript", "superscript", "indent", "outdent", "fontName"],
      ["fontSize", "textColor", "backgroundColor", "customClasses", "insertImage", "insertVideo"],
    ],
  };

  productForm!: FormGroup;
  productExpiryHintText!: string;
  private formControls: { [key in ProductControls]?: FormControl<any> } = {};

  productListingStatusOptions = [
    { key: ProductListingStatus.Active, value: "Active" },
    { key: ProductListingStatus.Inactive, value: "Inactive" },
    { key: ProductListingStatus.AuctionActive, value: "Auction Active" },
  ];

  countrySpecificForm = new FormGroup({});

  /** Zoned markets (US: five, MY: two) price delivery by the customer's state. */
  readonly deliveryZonesFor = deliveryZonesFor;
  readonly zoneControlName = zoneControlName;

  /**
   * This merchant's Delivery Information settings, by country — what a newly added
   * product country starts from. Loaded once in ngOnInit.
   */
  private merchantDeliveryDefaults: Record<
    string,
    { standardDeliveryFee: number; deliveryZoneFees?: ZoneFees | null }
  > = {};
  selectedCountries = signal<string[]>([]);
  collapsedCountries = signal<Set<string>>(new Set());
  private formSubscription: Subscription | null = null;

  originCountries = signal<Country[]>([]);
  destinationCountries = computed(() => this.appStateService.destinationCountries());

  categories = computed(() => this.appStateService.parentCategories());
  subCategories = signal<ICategoryList>([]);
  subSubCategories = signal<ICategoryList>([]);

  categoryOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.categories().map((c) => ({ value: c.id, label: c.category_name }))
  );

  subCategoryOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.subCategories().map((c) => ({ value: c.id, label: c.category_name }))
  );

  subSubCategoryOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.subSubCategories().map((c) => ({ value: c.id, label: c.category_name }))
  );

  temperamentOptions = computed<FormAutocompleteOption<string>[]>(() => [
    { value: "Aggressive", label: "Aggressive" },
    { value: "Semi Aggressive", label: "Semi Aggressive" },
    { value: "Peaceful", label: "Peaceful" },
  ]);

  waterConditionOptions: FormAutocompleteOption<string>[] = [
    { value: "High PH", label: "High PH" },
    { value: "Low PH", label: "Low PH" },
  ];

  wysiwygOptions: FormAutocompleteOption<string>[] = [
    { value: "exact", label: "Exact Fish Shown" },
    { value: "representative", label: "Representative Photo Only" },
  ];

  isGeneratingDescription = signal(false);

  booleanOptions = computed<FormAutocompleteOption<boolean>[]>(() => [
    { value: false, label: "No" },
    { value: true, label: "Yes" },
  ]);

  statusOptions = computed<FormAutocompleteOption<string>[]>(() =>
    this.productListingStatusOptions.map((s) => ({ value: s.key, label: s.value }))
  );

  minDate = new Date();
  maxDate = new Date(new Date().setDate(new Date().getDate() + 30));

  constructor(
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly requestService: RequestService,
    private readonly productService: ProductService,
    private readonly appStateService: AppStateService
  ) {}

  ngOnInit() {
    this.fetchOriginCountries();
    this.fetchAllCountries();
    this.fetchMerchantDeliveryDefaults();
    this.fetchAllowedMarkets();
    if (!this.productId) {
      this.buildForm({} as IProductResponseDto);
      this.getCategories();
    } else {
      this.loadExistingProduct();
    }
  }

  /**
   * Adopts a product id and rebuilds the form from what is actually stored.
   *
   * Called after a save now that the input page stays open instead of
   * navigating to the product list: images and videos the merchant added are
   * still flagged "add" in the in-memory form once they have been uploaded, so
   * saving a second time would upload every one of them again. Reloading
   * replaces those flags with the stored records.
   */
  /** Resolves once the form is rebuilt from what was stored (or the load failed). */
  reload(productId: number): Promise<void> {
    this.productId = productId;
    return this.loadExistingProduct();
  }

  /**
   * Fetches and rebuilds the form from the existing product's data (edit mode only).
   * Extracted out of ngOnInit so the page's refresh button (fh-refresh-button, wired via
   * ProductInputPageComponent's ViewChild) and the post-save reload above can re-trigger
   * the same load without duplicating this fetch logic.
   */
  loadExistingProduct(): Promise<void> {
    if (!this.productId) return Promise.resolve();
    return new Promise((resolve) => {
      this.requestService
        .get<IApiResponse<IProductResponseDto>>(`/api/v2/product?productId=${this.productId}`)
        .subscribe({
          next: (res) => {
            // finally: a throw while building must not leave Save disabled.
            try {
              if (res.status === true) {
                this.buildForm(res.data);
                this.categoryId = res.data.category_id;
                // getCategories() emits the segment once it can resolve the name.
                this.getCategories();
              } else {
                this.router.navigate(["/products"]);
              }
            } finally {
              resolve();
            }
          },
          error: () => resolve(),
        });
    });
  }

  fetchOriginCountries() {
    this.requestService
      .get<IApiResponse<Country[]>>(`/api/v2/origin-country-list`)
      .subscribe((res) => {
        this.originCountries.set(res.data);
      });
  }

  /**
   * Load the merchant's delivery settings so a new country's Delivery Charge (and US
   * zones) start from them. On a new product, any country added before they arrive
   * is filled in once they do — but only fields the merchant hasn't changed from 0.
   */
  /**
   * Who delivers in each market (admin portal > vendor > Delivery). The
   * Delivery Charge fields show only for Merchant Self Delivery; with FishyHub
   * Delivery (the default, also when unset) FishyHub manages the charge.
   */
  readonly deliveryModes = signal<Record<string, string>>({});

  /**
   * Markets FishyHub has enabled for this merchant (admin portal). Any other
   * market's checkbox is greyed out. null: not loaded, or no restriction.
   */
  readonly allowedMarkets = signal<string[] | null>(null);

  fetchAllowedMarkets() {
    this.requestService
      .get<IApiResponse<{ allowedMarkets: string[] | null }>>(`/api/v2/allowed-markets`)
      .subscribe((res) => {
        if (res.status === true) this.allowedMarkets.set(res.data?.allowedMarkets ?? null);
      });
  }

  /**
   * Markets the product was listed in when it was loaded. They stay allowed,
   * as on the server (blocking a market does not take products down), so one
   * unticked by accident can be ticked again.
   */
  private readonly loadedMarkets = signal<string[]>([]);

  isMarketAllowed(iso2: string): boolean {
    const allowed = this.allowedMarkets();
    const market = iso2.toUpperCase();
    return (
      !allowed ||
      allowed.some((a) => a.toUpperCase() === market) ||
      this.loadedMarkets().includes(market)
    );
  }

  /**
   * Markets the cross-border restrictions (admin portal) block for this
   * product's category from this store's country.
   */
  readonly restrictedMarkets = signal<string[]>([]);
  private restrictionSubscription: Subscription | null = null;

  isMarketRestricted(iso2: string): boolean {
    return this.restrictedMarkets().includes(iso2.toUpperCase());
  }

  /** Greyed out: not enabled for this store, or restricted for this category. */
  marketBlocked(iso2: string): boolean {
    return !this.isMarketAllowed(iso2) || this.isMarketRestricted(iso2);
  }

  marketBlockText(iso2: string): string {
    return this.isMarketRestricted(iso2)
      ? `Not allowed: cross-border restriction for this category into ${iso2.toUpperCase()}`
      : "Contact Fishyhub to enable this market";
  }

  /** Re-checks the restrictions whenever the category changes. */
  private watchRestrictions() {
    this.restrictionSubscription?.unsubscribe();
    const keys = [
      ProductControls.CategoryId,
      ProductControls.SubcategoryId,
      ProductControls.SubsubcategoryId,
    ];
    this.restrictionSubscription = this.productForm.valueChanges
      .pipe(
        startWith(this.productForm.value),
        map((v: Record<string, unknown>) => keys.map((k) => Number(v?.[k]) || 0)),
        distinctUntilChanged((a, b) => a.join() === b.join()),
        debounceTime(200),
        // switchMap drops a slower reply for a category no longer selected. A
        // failed request keeps what is shown and leaves the watch running.
        switchMap(([categoryId, subcategoryId, subsubcategoryId]) => {
          if (!categoryId) return of({ markets: [] as string[] });
          const query = new URLSearchParams({
            categoryId: String(categoryId),
            subcategoryId: String(subcategoryId),
            subsubcategoryId: String(subsubcategoryId),
          });
          return this.requestService
            .get<IApiResponse<{ markets: string[] }>>(`/api/v2/restricted-markets?${query}`)
            .pipe(
              filter((res) => res.status === true),
              map((res) => ({ markets: res.data?.markets ?? [] })),
              catchError(() => EMPTY)
            );
        })
      )
      .subscribe(({ markets }) => this.restrictedMarkets.set(markets));
  }

  showsDeliveryCharge(iso2: string): boolean {
    return this.deliveryModes()[iso2] === "merchant";
  }

  fetchMerchantDeliveryDefaults() {
    this.requestService
      .get<
        IApiResponse<
          Array<{
            iso2: string;
            standardDeliveryFee: number;
            deliveryZoneFees?: ZoneFees | null;
            deliveryMode?: string;
          }>
        >
      >(`/api/v2/delivery-info`)
      .subscribe((res) => {
        if (res.status !== true || !Array.isArray(res.data)) return;
        const modes: Record<string, string> = {};
        for (const row of res.data) modes[row.iso2] = row.deliveryMode ?? "fishyhub";
        this.deliveryModes.set(modes);
        for (const row of res.data) {
          this.merchantDeliveryDefaults[row.iso2] = {
            standardDeliveryFee: Number(row.standardDeliveryFee) || 0,
            deliveryZoneFees: row.deliveryZoneFees ?? null,
          };
        }
        if (this.productId) return; // saved products keep their own fees
        for (const iso2 of Object.keys(this.countrySpecificForm.controls)) {
          const group = this.countrySpecificForm.get(iso2) as FormGroup;
          const defaults = this.merchantDeliveryDefaults[iso2];
          if (!group || !defaults) continue;
          const zones = deliveryZonesFor(iso2);
          if (zones.length > 0) {
            for (const z of zones) {
              const ctrl = group.get(zoneControlName(z.zone));
              if (ctrl && !Number(ctrl.value)) {
                ctrl.setValue(
                  defaults.deliveryZoneFees?.[String(z.zone) as keyof ZoneFees] ??
                    defaults.standardDeliveryFee
                );
              }
            }
          } else {
            const ctrl = group.get("deliveryCharge");
            if (ctrl && !Number(ctrl.value)) ctrl.setValue(defaults.standardDeliveryFee);
          }
        }
      });
  }

  fetchAllCountries() {
    // Re-fetch even if APP_INITIALIZER already ran — the initializer may have
    // failed (unauthenticated) so we try again now that the user is logged in.
    if (this.appStateService.allCountries().length > 0) return;
    this.requestService.get<IApiResponse<Country[]>>(`/api/v2/country`).subscribe((res) => {
      if (res.status === true) {
        this.appStateService.allCountries.set(res.data);
      }
    });
  }

  buildForm(data: IProductResponseDto) {
    this.formControls = {};

    if (this.controls.includes(ProductControls.ProductName)) {
      this.formControls[ProductControls.ProductName] = new FormControl(data.product_name || "", [
        Validators.required,
        noContactInfo,
      ]);
    }
    // Optional everyday name; blank by default. When set, storefront product
    // cards read "<common name> (<product name>)".
    if (this.controls.includes(ProductControls.CommonName)) {
      this.formControls[ProductControls.CommonName] = new FormControl(data.common_name || "", [
        Validators.maxLength(120),
      ]);
    }
    if (this.controls.includes(ProductControls.Qty)) {
      this.formControls[ProductControls.Qty] = new FormControl(data.qty || 0, [
        Validators.required,
        Validators.pattern(phoneNumberPattern),
      ]);
    }
    if (this.controls.includes(ProductControls.MinSize)) {
      this.formControls[ProductControls.MinSize] = new FormControl(data.min_size || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }
    if (this.controls.includes(ProductControls.MaxSize)) {
      this.formControls[ProductControls.MaxSize] = new FormControl(data.max_size || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }
    if (this.controls.includes(ProductControls.IsPromoted)) {
      this.formControls[ProductControls.IsPromoted] = new FormControl(data.is_promoted || false, [
        Validators.required,
      ]);
    }
    if (this.controls.includes(ProductControls.OriginCountryIso2)) {
      this.formControls[ProductControls.OriginCountryIso2] = new FormControl(
        data.origin_iso2 || 0,
        [Validators.required, Validators.pattern(phoneNumberPattern)]
      );
    }
    if (this.controls.includes(ProductControls.VendorId)) {
      this.formControls[ProductControls.VendorId] = new FormControl(data.vendor_id || 0, [
        Validators.required,
      ]);
    }
    if (this.controls.includes(ProductControls.CategoryId)) {
      this.formControls[ProductControls.CategoryId] = new FormControl(data.category_id || 0, [
        Validators.required,
        Validators.pattern(phoneNumberPattern),
      ]);
      if (data.category_id) {
        this.getSubCategories(data.category_id);
      }
    }
    if (this.controls.includes(ProductControls.SubcategoryId)) {
      this.formControls[ProductControls.SubcategoryId] = new FormControl(data.subcategory_id || 0, [
        Validators.required,
        Validators.pattern(phoneNumberPattern),
      ]);
      if (data.subcategory_id) {
        this.getSubSubCategories(data.subcategory_id);
      }
    }
    if (this.controls.includes(ProductControls.SubsubcategoryId)) {
      this.formControls[ProductControls.SubsubcategoryId] = new FormControl(
        data.subsubcategory_id || 0,
        [Validators.required, Validators.pattern(phoneNumberPattern)]
      );
    }
    if (this.controls.includes(ProductControls.Status)) {
      this.formControls[ProductControls.Status] = new FormControl(
        data.status || ProductListingStatus.Active
      );
    }
    if (this.controls.includes(ProductControls.MoreDescription)) {
      this.formControls[ProductControls.MoreDescription] = new FormControl(
        data.more_description || "",
        noContactInfo
      );
    }
    if (this.controls.includes(ProductControls.IsPreOrder)) {
      this.formControls[ProductControls.IsPreOrder] = new FormControl(data.is_seasonal || false);
    }
    if (this.controls.includes(ProductControls.IsRequestQuote)) {
      this.formControls[ProductControls.IsRequestQuote] = new FormControl(
        (data as any).is_request_quote || false
      );
    }
    if (this.controls.includes(ProductControls.IsRareFish)) {
      this.formControls[ProductControls.IsRareFish] = new FormControl(
        (data as any).is_exclusive || false
      );
    }
    if (this.controls.includes(ProductControls.Temperament)) {
      this.formControls[ProductControls.Temperament] = new FormControl(data.temperament || "");
    }
    if (this.controls.includes(ProductControls.WaterCondition)) {
      this.formControls[ProductControls.WaterCondition] = new FormControl(
        data.water_condition || ""
      );
    }
    if (this.controls.includes(ProductControls.Wysiwyg)) {
      this.formControls[ProductControls.Wysiwyg] = new FormControl(
        (data as any).wysiwyg || "representative"
      );
    }
    if (this.controls.includes(ProductControls.ProductExpiry)) {
      this.formControls[ProductControls.ProductExpiry] = new FormControl(
        data.expires_at ? new Date(data.expires_at) : null
      );
      this.setupProductExpiry({ currentExpiry: data.expires_at });
    }
    if (this.controls.includes(ProductControls.TankBreadth)) {
      this.formControls[ProductControls.TankBreadth] = new FormControl(data.tank_breadth || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }
    if (this.controls.includes(ProductControls.TankHeight)) {
      this.formControls[ProductControls.TankHeight] = new FormControl(data.tank_height || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }
    if (this.controls.includes(ProductControls.TankLength)) {
      this.formControls[ProductControls.TankLength] = new FormControl(data.tank_length || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }
    if (this.controls.includes(ProductControls.CapacityInGallons)) {
      this.formControls[ProductControls.CapacityInGallons] = new FormControl(
        data.capacity_in_gallons || 0,
        [Validators.required, Validators.pattern(decimalNumber)]
      );
    }

    const media: IMediaPicker = {
      images: data.images?.map((i) => ({ ...i, state: "original" })) || [],
      videos: data.videos?.map((i) => ({ ...i, state: "original" })) || [],
    };
    if (this.controls.includes(ProductControls.ProductMedia)) {
      this.formControls[ProductControls.ProductMedia] = new FormControl(media || {});
    }

    // Initialise country-specific form
    this.countrySpecificForm = new FormGroup({});
    this.selectedCountries.set([]);
    this.loadedMarkets.set(Object.keys(data.countrySpecificInfo ?? {}).map((c) => c.toUpperCase()));
    this.collapsedCountries.set(new Set());

    if (data.countrySpecificInfo) {
      const countryGroups: Record<string, FormGroup> = {};
      Object.entries(data.countrySpecificInfo).forEach(([iso2, info]) => {
        this.selectedCountries.update((countries) => [...countries, iso2]);
        countryGroups[iso2] = this.createCountryFormGroup(iso2, info);
      });
      this.countrySpecificForm = new FormGroup(countryGroups);
    }

    this.productForm = this.formBuilder.group(this.formControls);
    this.watchRestrictions();

    // When isRequestQuote toggles, disable/enable displayPrice in all country forms
    if (this.controls.includes(ProductControls.IsRequestQuote)) {
      this.formControls[ProductControls.IsRequestQuote]?.valueChanges.subscribe((isRQ: boolean) => {
        this.applyRequestQuoteState(isRQ);
        const preOrderCtrl = this.formControls[ProductControls.IsPreOrder];
        if (isRQ) {
          preOrderCtrl?.setValue(false, { emitEvent: false });
          preOrderCtrl?.disable({ emitEvent: false });
        } else {
          preOrderCtrl?.enable({ emitEvent: false });
        }
      });

      // Apply on initial load (e.g. editing a product already set to Request Quote) so
      // Display Price and Pre-Order load greyed out / disabled, matching the toggle behaviour.
      const initialRQ = this.isRequestQuoteControl?.value ?? false;
      this.applyRequestQuoteState(initialRQ);
      const preOrderCtrl = this.formControls[ProductControls.IsPreOrder];
      if (initialRQ) {
        preOrderCtrl?.setValue(false, { emitEvent: false });
        preOrderCtrl?.disable({ emitEvent: false });
      } else {
        preOrderCtrl?.enable({ emitEvent: false });
      }
    }

    printFormValueAndValidity(this.productForm);

    // Unsubscribe from any previous subscription
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    this.formSubscription = combineLatest([
      this.productForm.valueChanges.pipe(startWith(this.productForm.value)),
      this.productForm.statusChanges.pipe(startWith(this.productForm.status)),
      this.countrySpecificForm.valueChanges.pipe(startWith(this.countrySpecificForm.value)),
      this.countrySpecificForm.statusChanges.pipe(startWith(this.countrySpecificForm.status)),
    ]).subscribe(([productFormValue, productFormStatus, _countryFormValue, countryFormStatus]) => {
      const { productMedia, tags, ...info } = productFormValue;

      const countrySpecificInfo = Object.keys(this.countrySpecificForm.controls).reduce<
        Record<string, any>
      >((acc, iso2) => {
        const countryForm = this.countrySpecificForm.get(iso2);
        if (countryForm) {
          // Use getRawValue so disabled fields (displayPrice, deliveryCharge, option price
          // when isRequestQuote=true) are included with their 0 values, not excluded.
          const formValue = (countryForm as any).getRawValue
            ? (countryForm as any).getRawValue()
            : countryForm.value;
          // Options marked state:"delete" must still be sent to the backend (it does
          // the actual DELETE FROM product_options based on state, not by diffing IDs)
          // -- filtering them out here silently drops the deletion on save.
          const productOptions = Array.isArray(formValue.productOptions)
            ? formValue.productOptions.map((opt: any) => ({
                id: opt.id || 0,
                addon_name: opt.addon_name,
                price: Number(opt.price),
                status: opt.status || "active",
                state: opt.state,
                // The bag / box it ships in; the backend sets its markup from it.
                packing_size: opt.packing_size ?? null,
              }))
            : [];

          acc[iso2] = {
            iso2,
            displayPrice: Number(formValue.displayPrice),
            deliveryCharge: Number(formValue.deliveryCharge),
            // Tells the backend this delivery charge was set on this form on purpose,
            // so it is saved rather than replaced by the stored fee. Not when the
            // field is hidden (FishyHub Delivery): the stored fee, or on a new
            // product the merchant's defaults, stays.
            deliveryChargeSubmitted: this.showsDeliveryCharge(iso2),
            ...(deliveryZonesFor(iso2).length > 0 && this.showsDeliveryCharge(iso2)
              ? { deliveryZoneFees: this.zoneFeesOf(iso2, countryForm as FormGroup) }
              : {}),
            destinationTranshipperMarkup: Number(formValue.destinationTranshipperMarkup),
            // Always sent, so ticking or unticking it on this form is what gets saved
            // (the backend keeps the stored flag only when it is absent).
            walkInOnly: Boolean(formValue.walkInOnly),
            productOptions,
          };
        }
        return acc;
      }, {});

      const convertedInfo = {
        ...info,
        qty: info.qty !== undefined ? Number(info.qty) : undefined,
        min_size: info.min_size !== undefined ? Number(info.min_size) : undefined,
        max_size: info.max_size !== undefined ? Number(info.max_size) : undefined,
      };

      this.formChanged.emit({
        ...convertedInfo,
        ...productMedia,
        tags: ((tags || []) as unknown as string[])?.join(","),
        countrySpecificInfo,
      });

      // Determine overall validity
      const hasCountries = Object.keys(countrySpecificInfo).length > 0;
      const isRQ = this.isRequestQuoteControl?.value ?? false;
      const allCountriesValid =
        hasCountries &&
        Object.values(countrySpecificInfo).every((country: any) => {
          // productOptions now includes state:"delete" entries (needed in the save
          // payload), so validity must count only the ones that will remain active.
          const opts = (country.productOptions || []).filter((opt: any) => opt.state !== "delete");
          const priceOk = isRQ || country.displayPrice > 0;
          return opts.length > 0 && priceOk;
        });

      const isValid =
        productFormStatus === "VALID" &&
        countryFormStatus === "VALID" &&
        (this.mediaPickerControl?.value.images?.filter((img: any) => img.state !== "delete")
          ?.length || 0) > 0 &&
        hasCountries &&
        allCountriesValid;

      this.formIsValid.emit(isValid);
    });
  }

  setupProductExpiry(data: { currentExpiry?: string }) {
    if (data.currentExpiry) {
      const { text } = this.productService.getExpiryDisplayText(data.currentExpiry);
      this.productExpiryHintText = text;
    }
    this.productExpiryControl?.valueChanges.subscribe((value) => {
      if (value) {
        const { text } = this.productService.getExpiryDisplayText(value);
        this.productExpiryHintText = text;
      } else {
        this.productExpiryHintText = "";
      }
    });
  }

  onDestinationCountryChange(event: MatCheckboxChange, iso2: string) {
    if (event.checked) {
      this.selectedCountries.update((countries) => [...countries, iso2]);
      // Ensure panel is expanded when first added
      this.collapsedCountries.update((s) => {
        const next = new Set(s);
        next.delete(iso2);
        return next;
      });
      const countryFormGroup = this.createCountryFormGroup(iso2);
      this.countrySpecificForm.addControl(iso2, countryFormGroup);
      // A country can be added after Request Quote was already toggled on, so apply the
      // current state to grey out / disable Display Price on the newly added group.
      this.applyRequestQuoteState(this.isRequestQuoteControl?.value ?? false);
    } else {
      this.selectedCountries.update((countries) => countries.filter((c) => c !== iso2));
      this.collapsedCountries.update((s) => {
        const next = new Set(s);
        next.delete(iso2);
        return next;
      });
      this.countrySpecificForm.removeControl(iso2);
    }
  }

  isCountryExpanded(iso2: string): boolean {
    return !this.collapsedCountries().has(iso2);
  }

  onPanelOpened(iso2: string) {
    this.collapsedCountries.update((s) => {
      const next = new Set(s);
      next.delete(iso2);
      return next;
    });
  }

  onPanelClosed(iso2: string) {
    this.collapsedCountries.update((s) => {
      const next = new Set(s);
      next.add(iso2);
      return next;
    });
  }

  getSelectedCountries(): Country[] {
    return this.destinationCountries()
      .filter((country) => this.isCountrySelected(country.iso2))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  getUnselectedCountries(): Country[] {
    return this.destinationCountries()
      .filter((country) => !this.isCountrySelected(country.iso2))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  isCountrySelected(iso2: string): boolean {
    return this.selectedCountries().some((c) => c === iso2);
  }

  isCountryFormInvalid(iso2: string): boolean {
    const countryGroup = this.countrySpecificForm.get(iso2) as FormGroup;
    if (!countryGroup) return true;

    const displayPrice = countryGroup.get("displayPrice");
    const deliveryCharge = countryGroup.get("deliveryCharge");
    const hasEmptyRequiredFields =
      !displayPrice ||
      displayPrice.value === null ||
      displayPrice.value === undefined ||
      !deliveryCharge ||
      deliveryCharge.value === null ||
      deliveryCharge.value === undefined;

    const productOptionsCtrl = countryGroup.get("productOptions");
    const options = Array.isArray(productOptionsCtrl?.value) ? productOptionsCtrl?.value : [];
    const hasValidOptions = options.some(
      (opt: any) => opt.state !== "delete" && !!opt.addon_name && Number(opt.price) > 0
    );

    return hasEmptyRequiredFields || !hasValidOptions;
  }

  shouldShowError(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getErrorMessage(control: AbstractControl | null): string {
    if (!control) return "";
    if (control.hasError("required")) return "This field is required";
    if (control.hasError("min")) return `Value must be at least ${control.getError("min").min}`;
    if (control.hasError("pattern")) return "Invalid format";
    return "";
  }

  getCountryControl(iso2: string, controlName: string): FormControl {
    const control = this.countrySpecificForm.get(`${iso2}.${controlName}`);
    if (!control) {
      return new FormControl([]);
    }
    return control as unknown as FormControl;
  }

  onCategorySelectionChange(categoryId: number) {
    this.subcategoryIdControl?.patchValue(0);
    if (this.subsubcategoryIdControl) {
      this.subsubcategoryIdControl.patchValue(0);
    }
    this.getSubCategories(categoryId);
  }

  onSubcategorySelectionChange(subCategoryId: number) {
    if (this.subsubcategoryIdControl) {
      this.subsubcategoryIdControl.patchValue(0);
    }
    this.getSubSubCategories(subCategoryId);
  }

  getCategories() {
    this.requestService
      .get(`/api/v2/categories?parent_id=0&status=active`)
      .subscribe((res: any) => {
        if (res.status !== true) return;
        const all = res.data ?? [];

        // Resolved here, against the freshly fetched names, because this is the
        // first point at which the category's name is known. `this.categoryId`
        // is a segment (1/5/0) for a new product and the product's real main
        // category for an existing one — both resolve correctly.
        const segment = getSegmentForCategory(this.categoryId, all);

        // Matched on name so every livestock main category — Freshwater and
        // Marine alike — offers the livestock form and its extra fields, and
        // none of them can be filed through the generic "Others" form.
        let filteredData = all;
        if (segment === LIVESTOCK_SEGMENT) {
          filteredData = all.filter((cat: any) => isLivestockCategoryName(categoryNameOf(cat)));
        } else if (segment === TANK_SEGMENT) {
          filteredData = all.filter((cat: any) => cat.id === TANK_SEGMENT);
        } else {
          filteredData = all.filter(
            (cat: any) => !isLivestockCategoryName(categoryNameOf(cat)) && cat.id !== TANK_SEGMENT
          );
        }
        this.appStateService.parentCategories.set(filteredData);

        // Only emitted for an existing product, whose real category needs
        // auto-detecting (so the page never briefly renders the wrong form for
        // a Marine Livestock product). For a new product this must stay silent:
        // the page's activeSegment() prefers this emission over the segment the
        // merchant is actively clicking through (categoryId), via `?? `. Once
        // set here, it never falls back to categoryId() again — even to a value
        // that's now stale — so emitting it on the new-product path (this method
        // also runs from ngOnInit before any product exists, to seed the category
        // dropdown for the default Livestock tab) silently froze every subsequent
        // Tank/Others tab click: categoryId updated correctly, but the page kept
        // rendering — and highlighting — Livestock regardless.
        if (this.productId) {
          this.productCategorySegment.emit(segment);
        }
      });
  }

  getSubCategories(categoryId: number) {
    this.requestService
      .get(`/api/v2/categories?parent_id=${categoryId}&status=active`)
      .subscribe((res: any) => {
        if (res.status === true) {
          const data = res.data.map((cat: any) => ({
            ...cat,
            name: cat.name || cat.category_name,
          }));
          this.subCategories.set(data);
        }
      });
  }

  getSubSubCategories(subCategoryId: number) {
    this.requestService
      .get(`/api/v2/categories?parent_id=${subCategoryId}&status=active`)
      .subscribe((res: any) => {
        if (res.status === true) {
          const data = res.data.map((cat: any) => ({
            ...cat,
            name: cat.name || cat.category_name,
          }));
          this.subSubCategories.set(data);
        }
      });
  }

  onExpirySelectionChange(expiryValue: string) {
    if (expiryValue) {
      this.productExpiryHintText = this.getExpiryTextWithDate(expiryValue);
    } else {
      this.productExpiryHintText = "Hit save to remove expiry.";
    }
  }

  // ─── Form control getters ────────────────────────────────────────────────

  get productNameControl(): FormControl<string> | undefined {
    return this.formControls[ProductControls.ProductName];
  }

  get commonNameControl(): FormControl<string> | undefined {
    return this.formControls[ProductControls.CommonName];
  }

  get qtyControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.Qty];
  }

  get minSizeControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.MinSize];
  }

  get maxSizeControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.MaxSize];
  }

  get isPromotedControl(): FormControl<boolean> | undefined {
    return this.formControls[ProductControls.IsPromoted];
  }

  /**
   * Whether a destination is the supplier country - the merchant's own, which
   * the backend gives every product. null until the merchant's country is known.
   */
  isSupplierCountry(iso2: string): boolean | null {
    const supplier = this.appStateService.merchantIso2();
    return supplier ? supplier === iso2.toUpperCase() : null;
  }

  get originCountryIso2Control(): FormControl<string> | undefined {
    return this.formControls[ProductControls.OriginCountryIso2];
  }

  get categoryIdControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.CategoryId];
  }

  get statusControl(): FormControl<string> | undefined {
    return this.formControls[ProductControls.Status];
  }

  get moreDescriptionControl(): FormControl<string> | undefined {
    return this.formControls[ProductControls.MoreDescription];
  }

  get subcategoryIdControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.SubcategoryId];
  }

  get subsubcategoryIdControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.SubsubcategoryId];
  }

  get isPreOrderControl(): FormControl<boolean> | undefined {
    return this.formControls[ProductControls.IsPreOrder];
  }

  get isRareFishControl(): FormControl<boolean> | undefined {
    return this.formControls[ProductControls.IsRareFish];
  }

  get isRequestQuoteControl(): FormControl<boolean> | undefined {
    return this.formControls[ProductControls.IsRequestQuote];
  }

  applyRequestQuoteState(isRQ: boolean): void {
    // Request Quote products carry no price and no delivery charge, so the delivery
    // fields (zones included) are zeroed and disabled alongside Display Price.
    const priceFields = ["displayPrice", "deliveryCharge", ...ALL_ZONE_CONTROL_NAMES];
    Object.keys(this.countrySpecificForm.controls).forEach((iso2) => {
      const group = this.countrySpecificForm.get(iso2);
      if (!group) return;
      for (const field of priceFields) {
        const ctrl = group.get(field);
        if (!ctrl) continue;
        if (isRQ) {
          ctrl.setValue(0, { emitEvent: false });
          ctrl.disable({ emitEvent: false });
        } else {
          ctrl.enable({ emitEvent: false });
        }
      }
    });
  }

  get wysiwygControl(): FormControl<string> | undefined {
    return this.formControls[ProductControls.Wysiwyg];
  }

  get temperamentControl(): FormControl<string> | undefined {
    return this.formControls[ProductControls.Temperament];
  }

  get waterConditionControl(): FormControl<string> | undefined {
    return this.formControls[ProductControls.WaterCondition];
  }

  get productExpiryControl(): FormControl<Date> | undefined {
    return this.formControls[ProductControls.ProductExpiry];
  }

  get tankBreadthControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.TankBreadth];
  }

  get tankLengthControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.TankLength];
  }

  get tankHeightControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.TankHeight];
  }

  get capacityControl(): FormControl<number> | undefined {
    return this.formControls[ProductControls.CapacityInGallons];
  }

  get mediaPickerControl(): FormControl<IMediaPicker> | undefined {
    return this.formControls[ProductControls.ProductMedia];
  }

  generateDescription() {
    const productName = this.productNameControl?.value?.trim();
    if (!productName) {
      alert("Please enter a product name before generating a description.");
      return;
    }

    this.isGeneratingDescription.set(true);

    const body: Record<string, any> = { product_name: productName };
    if (this.temperamentControl?.value) body.temperament = this.temperamentControl.value;
    if (this.waterConditionControl?.value) body.water_condition = this.waterConditionControl.value;
    if (this.minSizeControl?.value != null) body.min_size = this.minSizeControl.value;
    if (this.maxSizeControl?.value != null) body.max_size = this.maxSizeControl.value;

    this.requestService
      .post<{ status: boolean; data: { description: string }; message: string }>(
        "/api/v2/generate-description",
        body
      )
      .subscribe({
        next: (res) => {
          if (res.status && res.data?.description) {
            this.moreDescriptionControl?.setValue(res.data.description);
            this.moreDescriptionControl?.markAsDirty();
          } else {
            alert(res.message || "Failed to generate description.");
          }
          this.isGeneratingDescription.set(false);
        },
        error: () => {
          alert("An error occurred while generating the description.");
          this.isGeneratingDescription.set(false);
        },
      });
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  private getExpiryTextWithDate(date: string) {
    return `Expires on ${new Date(date).toLocaleDateString()} at 11:59PM`;
  }

  private createCountryFormGroup(iso2: string, existingData?: any): FormGroup {
    const productOptions = existingData?.productOptions || [];
    const amount = [Validators.required, Validators.min(0), Validators.pattern(decimalNumber)];
    // A newly added country starts from the merchant's Delivery Information settings.
    const defaults = existingData ? null : this.merchantDeliveryDefaults[iso2];
    const startingFee = existingData?.deliveryCharge ?? defaults?.standardDeliveryFee ?? 0;

    // Declared as a plain FormGroup: zoned markets gain zone controls below, which a
    // strictly typed group would reject by name.
    const group: FormGroup = new FormGroup({
      iso2: new FormControl(iso2, [Validators.required]),
      displayPrice: new FormControl(existingData?.displayPrice ?? 0, [
        Validators.required,
        Validators.min(0),
        Validators.pattern(decimalNumber),
      ]),
      deliveryCharge: new FormControl(startingFee, amount),
      destinationTranshipperMarkup: new FormControl(
        existingData?.destinationTranshipperMarkup ?? 0
      ),
      productOptions: new FormControl(productOptions),
      walkInOnly: new FormControl(existingData?.walkInOnly ?? false),
    });

    // A zoned market (US: five, MY: two) replaces the single Delivery Charge with zone
    // fees, charged at checkout by the customer's shipping-address state. deliveryCharge stays in the form,
    // hidden and held at the highest zone — the fallback checkout uses for a state it
    // cannot match. A product that predates zones starts every zone at its current
    // fee, so saving without editing leaves what customers pay unchanged.
    const zones = deliveryZonesFor(iso2);
    if (zones.length > 0) {
      const saved = (existingData?.deliveryZoneFees ??
        defaults?.deliveryZoneFees ??
        null) as ZoneFees | null;
      for (const z of zones) {
        group.addControl(
          zoneControlName(z.zone),
          new FormControl(saved?.[String(z.zone) as keyof ZoneFees] ?? startingFee, amount)
        );
      }
      const syncSingleFee = () =>
        group
          .get("deliveryCharge")
          ?.setValue(highestZoneFee(this.zoneFeesOf(iso2, group)), { emitEvent: false });
      for (const z of zones) {
        group.get(zoneControlName(z.zone))?.valueChanges.subscribe(syncSingleFee);
      }
      syncSingleFee();
    }

    // Applied after the zone controls exist, so ticking the box disables those
    // too - there is no zone to price when nothing ships.
    this.applyWalkInOnly(group, Boolean(existingData?.walkInOnly));
    group
      .get("walkInOnly")
      ?.valueChanges.subscribe((checked) => this.applyWalkInOnly(group, Boolean(checked)));

    return group;
  }

  /**
   * Collected in person, so there is nothing to deliver and nothing to charge for
   * it. The charge is zeroed and disabled rather than merely ignored: a number
   * left visible beside a ticked box reads as one that will still be billed. The
   * server zeroes it again on save, because a disabled control still submits
   * whatever value it holds.
   */
  private applyWalkInOnly(group: FormGroup, walkInOnly: boolean): void {
    const names = ["deliveryCharge", ...ALL_ZONE_CONTROL_NAMES];
    for (const name of names) {
      const control = group.get(name);
      if (!control) continue;
      if (walkInOnly) {
        control.setValue(0, { emitEvent: false });
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  /** A zoned country group's fees as {"1": n, ..., "n": n}. */
  private zoneFeesOf(iso2: string, group: FormGroup): ZoneFees {
    const fees: ZoneFees = {};
    for (const z of deliveryZonesFor(iso2)) {
      fees[String(z.zone) as keyof ZoneFees] =
        Number(group.get(zoneControlName(z.zone))?.value) || 0;
    }
    return fees;
  }

  ngOnDestroy(): void {
    this.restrictionSubscription?.unsubscribe();
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
      this.formSubscription = null;
    }
  }
}
